import { useState } from 'react';
import {
  formatCount,
  formatNumber,
  getAge,
  overallProgress,
  quoteAge,
  quotePrestige,
  visibleTechs,
} from '../engine';
import { useGame, useGameLoop } from '../game/useGame';
import { gameStore } from '../game/store';
import { OfflineModal } from './OfflineModal';
import { Sidebar } from './Sidebar';
import { AncestralPanel } from './panels/AncestralPanel';
import { BuildingsPanel } from './panels/BuildingsPanel';
import { JobsPanel } from './panels/JobsPanel';
import { PrestigePanel } from './panels/PrestigePanel';
import { ResourceBar } from './panels/ResourceBar';
import { SettingsPanel } from './panels/SettingsPanel';
import { TalesPanel } from './panels/TalesPanel';
import { TechPanel } from './panels/TechPanel';

type TabId = 'tribe' | 'camp' | 'discoveries' | 'memory' | 'fire' | 'tales' | 'settings';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'tribe', label: 'Tribe' },
  { id: 'camp', label: 'Camp' },
  { id: 'discoveries', label: 'Discoveries' },
  { id: 'memory', label: 'Memory' },
  { id: 'fire', label: 'The Fire' },
  { id: 'tales', label: 'Tales' },
  { id: 'settings', label: 'Settings' },
];

export function App() {
  useGameLoop();
  const snapshot = useGame();
  const [tab, setTab] = useState<TabId>('tribe');

  const { state } = snapshot;
  const age = getAge(state.ageIndex);
  const progress = overallProgress(state);
  const notation = state.settings.notation;

  // Badge counts nudge the player toward whatever is actually actionable.
  const affordableTechs = visibleTechs(state).filter(
    (view) =>
      view.available &&
      Object.entries(view.def.cost).every(
        ([resourceId, amount]) => (state.resources[resourceId] ?? 0) >= (amount ?? 0),
      ),
  ).length;
  const canPassFire = quotePrestige(state).canPass || quoteAge(state).canAdvance;

  return (
    <div className="app-shell">
      <header className="header">
        <div>
          <h1 className="header-title">Emberline</h1>
          <div className="header-age">
            {age.name} — <em>{age.subtitle}</em>
          </div>
        </div>

        <div className="header-stats">
          <div className="stat">
            <span className="stat-label">Tribe</span>
            <span className="stat-value accent">{formatCount(state.population)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Generation</span>
            <span className="stat-value">{state.generation}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Knowledge</span>
            <span className="stat-value">
              {formatNumber(state.ancestralKnowledge, notation)}
            </span>
          </div>
        </div>
      </header>

      <div className="bar" style={{ marginBottom: 16 }}>
        <div className="bar-fill" style={{ width: `${progress * 100}%` }} />
      </div>

      <ResourceBar snapshot={snapshot} />

      <nav className="tabs">
        {TABS.map((entry) => {
          const badge =
            entry.id === 'discoveries' && affordableTechs > 0
              ? affordableTechs
              : entry.id === 'fire' && canPassFire
                ? '!'
                : null;

          return (
            <button
              key={entry.id}
              type="button"
              className={`tab${tab === entry.id ? ' active' : ''}`}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
              {badge !== null ? <span className="badge">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      <div className="app-body">
        <main>
          {tab === 'tribe' ? <JobsPanel snapshot={snapshot} /> : null}
          {tab === 'camp' ? <BuildingsPanel snapshot={snapshot} /> : null}
          {tab === 'discoveries' ? <TechPanel snapshot={snapshot} /> : null}
          {tab === 'memory' ? <AncestralPanel snapshot={snapshot} /> : null}
          {tab === 'fire' ? <PrestigePanel snapshot={snapshot} /> : null}
          {tab === 'tales' ? <TalesPanel snapshot={snapshot} /> : null}
          {tab === 'settings' ? <SettingsPanel snapshot={snapshot} /> : null}
        </main>

        <Sidebar snapshot={snapshot} />
      </div>

      <p className="footer-note">
        Emberline — an idle game about keeping a fire alight. Saves live in this browser.
      </p>

      {gameStore.recentTales.length > 0 ? (
        <div className="toast-stack">
          {gameStore.recentTales.map((tale) => (
            <div
              key={`${tale.id}-${tale.at}`}
              className={`toast${tale.favourable ? '' : ' grim'}`}
              onClick={() => gameStore.dismissTale(tale.at)}
              role="status"
            >
              <div className={`tale-title ${tale.favourable ? 'favourable' : 'grim'}`}>
                {tale.title}
              </div>
              <p className="tale-text">{tale.text}</p>
              {tale.summary ? <div className="tale-meta">{tale.summary}</div> : null}
            </div>
          ))}
        </div>
      ) : null}

      {gameStore.offlineReport !== null ? (
        <OfflineModal report={gameStore.offlineReport} notation={notation} />
      ) : null}
    </div>
  );
}
